/* eslint-disable prefer-const */
import { Form, Input, Modal, Select, Spin, Tooltip, Typography } from "antd";
import { ethers } from "ethers";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";

import { useGetInfoByUserCode } from "../../../api/profile";
import {
  useCryptoWithdraw,
  useGetAvlBalanceInternal,
  useGetFeeAmount,
  useGetMinimumInternal,
  useValidateAddress,
} from "../../../api/withdrawal";
import { ReactComponent as CheckIcon } from "../../../common/images/svg/Check.svg";
import { ReactComponent as CheckSelectIcon } from "../../../common/images/svg/CheckSelect.svg";
import { ReactComponent as CloseIcon } from "../../../common/images/svg/CloseIcon.svg";
import { ReactComponent as NotiWarningIcon } from "../../../common/images/svg/NotiWarning.svg";
import { ReactComponent as ArrowIcon } from "../../../common/images/svg/SuffixSelectIcon.svg";
import { CustomButton } from "../../../components/button/buttonCustom";
import { SmartUidAutoCompleteInput } from "../../../components/inputs/autocomplete/smartUidAutocomplete";
import LoadingOverlay from "../../../components/loading/loadingOverlay";
import ConfirmModal from "../../../components/modal/confirmModal";
import { useNotification } from "../../../components/providers/notification/useNotification";
import { clearFieldErrorMessageOnChange } from "../../../utils/form.constants";
import { useMetadataCurrencyToken } from "../../../utils/hooks/useMetadataCurrencyToken";
import { useMetadataNetwork } from "../../../utils/hooks/useMetadataNetwork";
import useResponsive from "../../../utils/hooks/useResponsive";
import { createTronWebInstance } from "../../../utils/tronWeb";
import {
  formatNumber,
  hasLeadingDecimalZeros,
} from "../../../utils/list.utils";
import { ButtonTransferMode } from "./button-transfer-mode";
import InputAmount from "./input-amount";
import ModalConfirm from "./modal-confirm";
import smartLogoDefault from "../../../common/images/svg/smart-logo-icon.svg";

const { Option } = Select;
const { TextArea } = Input;

interface ModalWithdrawProps {
  selectedItem: any;
  prevSelectedItem: any;
  setSelectedItem: React.Dispatch<any>;
  setPrevSelectedItem: React.Dispatch<any>;
  setOpenModalSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setResponseWithdrawal: React.Dispatch<any>;
  refetchCryptoCurrency: any;
  refetchTotalAsset: any;
  socket: Socket<any, any>;
}

const requiredFields = [
  "assetConfigGroupId",
  "networkId",
  "withdrawAddress",
  "amount",
];

const requiredFieldsInternal = ["assetConfigGroupId", "smartUID", "amount"];

const ModalWithdraw = ({
  selectedItem,
  setSelectedItem,
  setOpenModalSuccess,
  setResponseWithdrawal,
  refetchCryptoCurrency,
  refetchTotalAsset,
  socket,
}: ModalWithdrawProps) => {
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [valueConfirm, setValueConfirm] = useState<any>();
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [symbolCurrency, setSymbolCurrency] = useState<any>();

  const [selectedNetwork, setSelectedNetwork] = useState<any>();
  const [transferMode, setTransferMode] = useState("SEND_VIA_CRYPTO_NETWORK");
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);
  const [totalBalance, setTotalBalance] = useState<any>(null);
  const [decimals, setDecimals] = useState<number | null>(null);
  const [responseFee, setResponseFee] = useState<any>(null);
  const [amountValue, setAmountValue] = useState<any>();
  const [addressValue, setAddressValue] = useState<any>();
  const [userCode, setUserCode] = useState<any>();
  const [minimumWithdrawal, setMinimumWithdrawal] = useState<any>("0");
  const [feeAmount, setFeeAmount] = useState<any>(0);
  const [submitFee, setSubmitFee] = useState<any>(0);
  const [prevVersion, setPrevVersion] = useState<any>(0);
  const [idFee, setIdFee] = useState<any>(0);
  const [warningChange, setWarningChange] = useState(false);
  const [beneficiaryGet, setBeneficiaryGet] = useState<any>(0);
  const [failedMessage, setFailedMessage] = useState<any>("");
  const [errorFee, setErrorFee] = useState("");
  const [errorAmount, setErrorAmount] = useState("");
  const [inputAmount, setInputAmount] = useState(false);
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null);
  const [tronWeb, setTronWeb] = useState<any>(null);
  const [addressBlur, setAddressBlur] = useState(false);
  const [amountBlur, setAmountBlur] = useState(false);
  const [apiErrorSmartUID, setApiErrorSmartUID] = useState<string | null>(null);
  const [nextTimeUpdate, setNextTimeUpdate] = useState<any>(null);
  const [now, setNow] = useState(Date.now());

  const currency = Form.useWatch("assetConfigGroupId", form);
  const network = Form.useWatch("networkId", form);
  const withdraw_address = Form.useWatch("withdrawAddress", form);
  const amount = Form.useWatch("amount", form);
  const smartUID = Form.useWatch("smartUID", form);

  const { dataCurrencyToken } = useMetadataCurrencyToken();
  const { metadataNetwork } = useMetadataNetwork(Number(selectedCurrency || 0));

  const {
    data: avlBalance,
    isFetching: loadingAvlBalance,
    refetch,
  } = useGetAvlBalanceInternal(Number(selectedCurrency || 0));

  const { data: minimumInternal, isLoading: loadingMinimumInternal } =
    useGetMinimumInternal(selectedCurrency, transferMode);

  const { mutate: fetchInfoByUserCode, isLoading: loadingUserCode } =
    useGetInfoByUserCode({
      onSuccess: (response: { data: { data: any } }) => {
        setInfoByUserCode(response?.data?.data);
        setApiErrorSmartUID(null);
        form.setFields([
          {
            name: "smartUID",
            errors: undefined,
          },
        ]);
      },
      onError: (error: { message: string; reason_code: string }) => {
        setInfoByUserCode(
          error?.reason_code === "ERR00010"
            ? "This SMART UID does not exist in SMART+"
            : error?.message
        );
        const errorMessage = error?.reason_code === "ERR00010"
          ? "This SMART UID does not exist in SMART+"
          : error?.message || "";
        setApiErrorSmartUID(errorMessage);
        if (error?.reason_code === "ERR00010") {
          form.setFields([
            {
              name: "smartUID",
              errors: ["This SMART UID does not exist in SMART+"],
            },
          ]);
        } else {
          form.setFields([
            {
              name: "smartUID",
              errors: [error?.message || ""],
            },
          ]);
        }
      },
    });

  const { mutate: cryptoWithdraw, isLoading } = useCryptoWithdraw({
    onSuccess: (response: {
      data: {
        data: {
          id: number;
          orderId: string;
          status: string;
          amount: string;
          currency: string;
          createdAt: string;
          address: string;
          beneficiaryRegNo: string;
          beneficiaryFullName: string;
          beneficiaryCompanyAddress: string;
          beneficiaryCountry: string;
          chainType: string;
          fee: string;
          decimals: number;
          printableFormUrl: string;
        };
        message: string;
      };
    }) => {
      form.resetFields();
      setSelectedCurrency(null);
      setValueConfirm(null);
      setSelectedNetwork(null);
      setFeeAmount(0);
      setPrevVersion(0);
      setIdFee(0);
      setAmountValue(undefined);
      setMinimumWithdrawal("0");
      setTotalBalance(null);
      setDecimals(null);
      setTransferMode("SEND_VIA_CRYPTO_NETWORK");
      form?.setFieldValue("amount", undefined);
      form.resetFields();
      setSelectedItem(null);
      setOpenModalSuccess(true);
      refetchCryptoCurrency();
      refetchTotalAsset();
      setInputAmount(false);
      setResponseWithdrawal(response.data.data);
      setErrorFee("");
      setResponseFee(null);
      if (openNotification) {
        openNotification({
          key: "create_success",
          message: "Withdrawal order submitted successfully!",
          type: "success",
        });
      }
    },
    onError: (erros: { message: string }) => {
      setFailedMessage(
        erros?.message ||
        "Withdrawal failed. Please check the details and try again!"
      );
    },
  });

  const { mutate: getFeeAmount, isLoading: loadingFee } = useGetFeeAmount({
    onSuccess: (response: {
      data: {
        data: [
          {
            fee: number;
            minWithdraw: number;
            version: number;
            nextTimeUpdate: string;
          },
        ];
      };
    }) => {
      setResponseFee(response?.data?.data);
      setErrorFee("");
      const earliest = response?.data?.data?.reduce((min, current) => {
        return new Date(current.nextTimeUpdate) < new Date(min.nextTimeUpdate)
          ? current
          : min;
      });
      setNextTimeUpdate(earliest?.nextTimeUpdate);
    },
    onError: (error: { message: string; reason_code: string }) => {
      setFeeAmount(0);
      setErrorFee(error?.message);
    },
  });

  const { mutateAsync: validateAddress, isLoading: loadingValidate } =
    useValidateAddress({
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === "ERR00102") {
          form.setFields([
            {
              name: "withdrawAddress",
              errors: [error?.message],
            },
            {
              name: "smartUID",
              errors: [error?.message],
            },
          ]);
          setInfoByUserCode(null);
        } else if (error?.reason_code === "ERR00103") {
          form.setFields([
            {
              name: "amount",
              errors: [""],
            },
          ]);
          setErrorAmount(error?.message);
        } else {
          setErrorFee(error?.message);
        }
      },
    });

  const onSubmitWithdraw = async () => {
    const body = {
      currencyId: selectedCurrency,
    };
    await getFeeAmount(body);
    if (feeAmount !== submitFee) setWarningChange(true);
    else {
      setSelectedItem(null);
      cryptoWithdraw({
        ...valueConfirm,
        ...(transferMode === "SEND_VIA_CRYPTO_NETWORK" && {
          feeConfigId: Number(idFee),
        }),
      });
    }
  };

  const onConfirmWithdraw = async (values: any) => {
    try {
      await validateAddress({
        address: values.withdrawAddress,
        smartUID: (values?.smartUID || "")?.trim(),
        amount: Number(values?.amount),
        receiveAmount: Number(
          (Number(values?.amount) - Number(feeAmount)).toFixed(
            decimals || selectedItem?.decimals
          )
        ),
        fee: Number(feeAmount),
        currencyId: Number(selectedCurrency),
      });

      if (loadingFee) return;
      const rootBody = {
        withdrawMethod: transferMode,
        currencyId: values.assetConfigGroupId,
        amount: Number(values.amount),
        note: values.note,
      };

      const modeCrypto = {
        networkId: values.networkId,
        withdrawAddress: values.withdrawAddress,
        feeConfigId: Number(idFee),
      };

      const modeInternal = {
        smartUID: values.smartUID,
      };

      const body = {
        ...rootBody,
        ...(transferMode === "SEND_VIA_CRYPTO_NETWORK"
          ? { ...modeCrypto }
          : { ...modeInternal }),
      };

      setSubmitFee(feeAmount);
      setValueConfirm(body);
      setSelectedItem(null);
      form?.resetFields(["smartUID", "amount"]);
    } catch (error: any) {
      console.error("Validate address failed", error);
    }
  };

  const handleContinueReview = () => {
    setSubmitFee(feeAmount);
    setWarningChange(false);
    if (Number(amountValue) < Number(minimumWithdrawal))
      setFailedMessage(
        `Your withdrawal amount must be at least ${formatNumber(
          Number(minimumWithdrawal),
          decimals || selectedItem?.decimals
        )}`
      );
  };

  const handleCancelModal = () => {
    navigate("/home");
    setSelectedCurrency(null);
    setSelectedNetwork(null);
    setFeeAmount(0);
    setPrevVersion(0);
    setIdFee(0);
    setAmountValue(undefined);
    setMinimumWithdrawal("0");
    setTotalBalance(null);
    setDecimals(null);
    setResponseFee(null);
    setTransferMode("SEND_VIA_CRYPTO_NETWORK");
    form?.setFieldValue("amount", undefined);
    form.resetFields();
    setInputAmount(false);
    setSelectedItem(null);
    setErrorFee("");
  };

  const handleWithdrawalAll = () => {
    if (
      (!selectedNetwork && transferMode === "SEND_VIA_CRYPTO_NETWORK") ||
      totalBalance === 0 ||
      totalBalance < Number(minimumWithdrawal)
    )
      return;
    if (!totalBalance) return;
    const factor = Math.pow(10, decimals || 4);
    const amount = (
      Math.floor(Number((totalBalance * factor).toFixed())) / factor
    ).toFixed(decimals || 4);
    form?.setFieldValue("amount", amount);
    form?.validateFields(["amount"]);
    setAmountValue(amount);
  };

  const handleChangeMethod = (method: string) => {
    setTransferMode(method);
    form.resetFields(["amount", "smartUID", "withdrawAddress", "networkId"]);
    setAmountValue(undefined);
    setInputAmount(false);
    setErrorAmount("");
    setUserCode(undefined);
    setInfoByUserCode(null);
    setApiErrorSmartUID(null);
    setSelectedNetwork(null);
  };

  const handleChangeCurrency = (value: any) => {
    setSelectedCurrency(value);
    form.resetFields(["amount", "withdrawAddress", "networkId", "smartUID"]);
    setInfoByUserCode(null);
    setApiErrorSmartUID(null);
    setAmountValue(undefined);
    setFeeAmount(0);
    setPrevVersion(0);
    setIdFee(0);
    setSelectedNetwork(null);
    setInputAmount(false);
    setMinimumWithdrawal("0");
  };

  const handleCancelConfirm = () => {
    setFailedMessage(null);
    setValueConfirm(null);
    setSubmitFee(0);
    setWarningChange(false);
    setSelectedItem(symbolCurrency);
    form?.setFieldValue("smartUID", userCode);
    form?.setFieldValue("amount", amountValue);
  };

  const handleTronWeb = async () => {
    try {
      // Wait for TronWebProto to be available (loaded by TronWeb.js script)
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)
      while (
        typeof globalThis !== 'undefined' &&
        !(globalThis as any).TronWebProto &&
        attempts < maxAttempts
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof globalThis === 'undefined' || !(globalThis as any).TronWebProto) {
        console.warn("TronWebProto not available, TronWeb functionality may be limited");
      }

      const { TronWeb } = await import("tronweb");
      const tronWebInstance = new TronWeb({
        fullHost: "https://api.trongrid.io",
      });
      setTronWeb(tronWebInstance);
    } catch (error) {
      console.error("Failed to initialize TronWeb:", error);
      // Set tronWeb to null to prevent further errors
      setTronWeb(null);
    }
  };

  const isValidAddressByNetwork = (
    address: string,
    network: number
  ): boolean => {
    if (!address) return false;
    switch (network) {
      case 1:
      case 2:
        return ethers.isAddress(address);
      case 3:
        return tronWeb?.isAddress(address);
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!selectedCurrency) return;
    const symbolCurrency = dataCurrencyToken.find(
      (item: any) => item.id === selectedCurrency
    ) as any;
    setSymbolCurrency(symbolCurrency);
    setSelectedItem(symbolCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    if (!addressValue || !network || !addressBlur) return;
    form.validateFields(["withdrawAddress"]);
  }, [network, addressValue, addressBlur]);

  useEffect(() => {
    if (loadingFee) return;
    if (selectedNetwork && responseFee && responseFee?.length > 0) {
      const fee = responseFee.find(
        (item: any) => item.networkId === selectedNetwork
      );
      setFeeAmount(fee?.fee);
      setPrevVersion(fee?.version);
      setIdFee(fee?.id);
      setMinimumWithdrawal(fee?.minWithdraw);
    }
  }, [selectedNetwork, responseFee, loadingFee]);

  useEffect(() => {
    if (!minimumInternal || loadingMinimumInternal) return;
    setMinimumWithdrawal(minimumInternal?.data?.data);
    setFeeAmount(0);
  }, [minimumInternal, loadingMinimumInternal]);

  const bodyGetFee = {
    currencyId: selectedCurrency,
  };

  //Fetching Fee
  useEffect(() => {
    if (!selectedCurrency || transferMode === "SMART_USER") return;
    getFeeAmount(bodyGetFee);
  }, [selectedCurrency, transferMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!nextTimeUpdate || !selectedCurrency) return;
    const nextTime =
      typeof nextTimeUpdate === "number"
        ? nextTimeUpdate
        : new Date(nextTimeUpdate).getTime();
    const diff = Math.abs(now - nextTime);
    if (diff <= 5000) {
      getFeeAmount(bodyGetFee);
    }
  }, [now, nextTimeUpdate, selectedCurrency]);

  //Validate amount
  useEffect(() => {
    if (
      minimumWithdrawal !== undefined &&
      minimumWithdrawal !== null &&
      form?.getFieldValue("amount")
    ) {
      if (Number(minimumWithdrawal) > Number(amountValue))
        form.validateFields(["amount"]);
    }
  }, [minimumWithdrawal]);

  //Calculate net received
  useEffect(() => {
    if (!amountBlur) return;
    if (
      Number(amountValue) > Number(feeAmount) &&
      Number(amountValue) >= Number(minimumWithdrawal) &&
      Number(amountValue) <= Number(totalBalance)
    ) {
      const beneficiaryAmount =
        Number(amountValue || 0) - Number(feeAmount || 0);
      setBeneficiaryGet(beneficiaryAmount);
    } else setBeneficiaryGet(0);
    if (inputAmount && !amount) setErrorAmount("This field is required");
    else if (Number(amountValue) < Number(minimumWithdrawal))
      setErrorAmount(
        `Your withdrawal amount must be at least ${formatNumber(
          Number(minimumWithdrawal),
          decimals || selectedItem?.decimals
        )}`
      );
    else if (Number(amountValue) > Number(totalBalance))
      setErrorAmount("Available balance is not sufficient for withdrawal");
    else setErrorAmount("");
  }, [
    amountValue,
    feeAmount,
    totalBalance,
    minimumWithdrawal,
    amount,
    amountBlur,
  ]);

  useEffect(() => {
    if (!selectedItem) return;
    form.setFieldValue("assetConfigGroupId", selectedItem?.id);
    setSelectedCurrency(selectedItem?.id);
  }, [selectedItem]);

  //Set total balance
  useEffect(() => {
    if (!avlBalance) return;
    setTotalBalance(avlBalance.data?.data?.totalBalance || 0);
    setDecimals(avlBalance?.data?.data?.decimals || 0);
  }, [avlBalance, selectedCurrency, loadingAvlBalance]);

  useEffect(() => {
    const fields =
      transferMode === "SEND_VIA_CRYPTO_NETWORK"
        ? requiredFields
        : requiredFieldsInternal;
    const values = form.getFieldsValue(fields);
    const allFilled = fields.every((field) => !!values[field]);
    setIsAllFieldsFilled(allFilled);
  }, [currency, network, withdraw_address, amount, smartUID, transferMode]);

  const handleBlur = async () => {
    const code = form.getFieldValue("smartUID")
      ? form.getFieldValue("smartUID")?.trim()
      : "";

    if (!code) {
      form.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
      setInfoByUserCode(null);
      return;
    }
    await fetchInfoByUserCode(code);
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    const fieldName = Object.keys(changedValues)[0];
    if (fieldName === "smartUID") {
      const v = changedValues.smartUID;
      setUserCode(v);
      if (v === undefined || v === null || String(v).trim() === "") {
        setApiErrorSmartUID(null);
        setInfoByUserCode(null);
        form.setFields([{ name: "smartUID", errors: undefined }]);
      } else {
        setApiErrorSmartUID(null);
        setInfoByUserCode(null);
      }
      return;
    }
    clearFieldErrorMessageOnChange(form)(changedValues);
  };

  useEffect(() => {
    if (selectedItem) {
      // Add a small delay to ensure TronWeb is loaded
      const timer = setTimeout(() => {
        handleTronWeb();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (!selectedItem) return;
    const handleWalletUpdated = () => {
      refetch();
    };
    socket.on(`user.wallet.updated`, handleWalletUpdated);

    return () => {
      socket.off(`user.wallet.updated`, handleWalletUpdated);
    };
  }, [socket, refetch, selectedItem]);

  return (
    <>
      {selectedItem ? (
        <Modal
          open={!!selectedItem && !isLoading}
          onCancel={handleCancelModal}
          footer={null}
          destroyOnClose
          maskClosable={false}
          title={
            <>
              <Typography
                style={{
                  fontSize: isTablet ? 16 : 20,
                  fontWeight: 700,
                  color: "#C2C6CE",
                }}
              >
                {"Withdraw"}
              </Typography>
              <CloseIcon
                onClick={handleCancelModal}
                className="icon-animation"
              />
            </>
          }
          className="withdraw-modal"
          style={{ display: valueConfirm ? "none" : "block" }}
        >
          <Form
            form={form}
            onFinish={onConfirmWithdraw}
            onValuesChange={handleValuesChange}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isTablet ? "18px" : "24px",
                height: "100% ",
                padding: isTablet ? "16px 16px 0px 16px" : "24px 24px 0px 24px",
              }}
            >
              <Form.Item
                label="Currency"
                name="assetConfigGroupId"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Select
                  placeholder="Choose a currency"
                  suffixIcon={<ArrowIcon />}
                  popupClassName="custom-select-dropdown"
                  onChange={handleChangeCurrency}
                  className="select-custom-withdraw"
                >
                  {Array.isArray(dataCurrencyToken) &&
                    dataCurrencyToken.map((item: any) => (
                      <Option key={item?.id} value={item.id}>
                        <div className="currency-option">
                          <img
                            src={
                              `${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`
                            }
                            onError={(e) => {
                              e.currentTarget.src = smartLogoDefault;
                            }}
                            style={{
                              objectFit: "cover",
                              width: isTablet ? 24 : 32,
                              height: isTablet ? 24 : 32,
                              borderRadius: "50%",
                            }}
                            alt="currency"
                          />
                          <span>{item.symbol}</span>
                        </div>
                        {selectedCurrency === item.id && <CheckIcon />}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              <div className="transfer-mode">
                <Typography className="transfer-mode-text">
                  Withdraw Method
                </Typography>
                <div className="transfer-mode-list-button">
                  <ButtonTransferMode
                    size={isTablet ? "small" : "medium"}
                    active={transferMode === "SEND_VIA_CRYPTO_NETWORK"}
                    text="Crypto Network"
                    width={"fit-content"}
                    onClick={() => {
                      handleChangeMethod("SEND_VIA_CRYPTO_NETWORK");
                      setMinimumWithdrawal("0");
                    }}
                  />
                  <ButtonTransferMode
                    size={isTablet ? "small" : "medium"}
                    active={transferMode === "SMART_USER"}
                    text="SMART UID"
                    width={"fit-content"}
                    onClick={() => handleChangeMethod("SMART_USER")}
                  />
                </div>
              </div>
              {transferMode === "SEND_VIA_CRYPTO_NETWORK" ? (
                <>
                  <Form.Item
                    label="Destination address"
                    name="withdrawAddress"
                    validateTrigger="onBlur"
                    rules={[
                      {
                        required: true,
                        message: "This field is required",
                        validateTrigger: "onBlur",
                      },
                      {
                        max: 256,
                        message: "Maximum 256 characters",
                        validateTrigger: "onBlur",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (value && !network) return Promise.resolve();
                          if (
                            value &&
                            isValidAddressByNetwork(value, network)
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Invalid address for selected network")
                          );
                        },
                      },
                    ]}
                  >
                    <TextArea
                      onChange={(e) => setAddressValue(e?.target.value)}
                      autoSize={{ minRows: 1, maxRows: 2 }}
                      placeholder="Enter the wallet address"
                      className="withdraw-address"
                      onBlur={() => setAddressBlur(true)}
                      onFocus={() => setAddressBlur(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Network"
                    name="networkId"
                    validateTrigger="onBlur"
                    rules={[
                      { required: true, message: "This field is required" },
                    ]}
                  >
                    <Select
                      className="select-network-withdraw"
                      placeholder="Select the blockchain network"
                      suffixIcon={<ArrowIcon />}
                      onChange={(value) => {
                        setSelectedNetwork(value);
                      }}
                      optionLabelProp="label"
                      popupClassName="custom-select-dropdown"
                    >
                      {metadataNetwork.map((item: any) => (
                        <Select.Option
                          key={item?.value}
                          value={item?.value}
                          label={item.label}
                        >
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {item?.label}
                            </span>
                            {selectedNetwork === item?.value && (
                              <CheckSelectIcon style={{ flexShrink: 0 }} />
                            )}
                          </span>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              ) : (
                <div>
                  <Form.Item
                    label="SMART UID"
                    name="smartUID"
                    validateTrigger="onBlur"
                    rules={[
                      {
                        required: true,
                        message: "This field is required",
                      },
                      {
                        max: 256,
                        message: "Maximum 256 characters",
                        validateTrigger: "onBlur",
                      },
                      {
                        validator: (_, value) => {
                          // Preserve API error if exists
                          if (apiErrorSmartUID) {
                            return Promise.reject(new Error(apiErrorSmartUID));
                          }
                          return Promise.resolve();
                        },
                        validateTrigger: "onBlur",
                      },
                    ]}
                  >
                    <SmartUidAutoCompleteInput
                      className="smart-uid-address"
                      placeholder="Enter the code linked to your SMART account (min. 3 characters)"
                      onBlur={() => {
                        void handleBlur();
                        if (apiErrorSmartUID) {
                          setTimeout(() => {
                            form.setFields([
                              {
                                name: "smartUID",
                                errors: [apiErrorSmartUID],
                              },
                            ]);
                          }, 0);
                        }
                      }}
                      onSuggestionSelect={(uid) => fetchInfoByUserCode(uid)}
                    />
                  </Form.Item>
                  {infoByUserCode?.email ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        marginTop: 8,
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          fontWeight: 500,
                          color: "white",
                        }}
                      >
                        Company name: {infoByUserCode?.fullName}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          fontWeight: 500,
                          color: "white",
                        }}
                      >
                        Reg. No: {infoByUserCode?.regNo}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          fontWeight: 500,
                          color: "white",
                        }}
                      >
                        Country: {infoByUserCode?.country}
                      </Typography>
                    </div>
                  ) : null}
                </div>
              )}
              <Form.Item
                style={{ position: "relative" }}
                label={`Amount`}
                name="amount"
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: "" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (Number(value) < Number(minimumWithdrawal)) {
                        return Promise.reject(new Error());
                      }
                      if (Number(value) > totalBalance) {
                        return Promise.reject(new Error());
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <div className="input-amount">
                  <InputAmount
                    placeholder={`Minimum withdrawal amount: ${formatNumber(
                      Number(minimumWithdrawal),
                      decimals || selectedItem?.decimals
                    )}`}
                    value={amountValue}
                    estDecimals={decimals}
                    onChange={(e) => {
                      form?.setFieldValue("amount", e?.target.value);
                      setAmountValue(e?.target.value);
                      setInputAmount(true);
                    }}
                    onBlur={() => {
                      setAmountBlur(true);
                      if (inputAmount) form?.validateFields(["amount"]);
                    }}
                    onFocus={() => setAmountBlur(false)}
                    disabled={
                      (!selectedNetwork &&
                        transferMode === "SEND_VIA_CRYPTO_NETWORK") ||
                      totalBalance === 0 ||
                      totalBalance < Number(minimumWithdrawal) ||
                      // loadingFee ||
                      (transferMode === "SMART_USER" && loadingMinimumInternal)
                    }
                  />
                  <div className="text-network-fee">
                    {feeAmount ? (
                      <Tooltip
                        placement="top"
                        title={
                          <span>
                            {feeAmount} {symbolCurrency?.symbol || ""}
                          </span>
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Typography.Text
                            style={{
                              fontSize: isTablet ? 12 : 14,
                              fontWeight: 400,
                              color: "white",
                            }}
                          >
                            Fees:{" "}
                            {formatNumber(
                              Number(feeAmount),
                              decimals || selectedItem?.decimals || 4
                            )}{" "}
                            {symbolCurrency?.symbol || ""}
                          </Typography.Text>
                          {loadingFee ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Spin size={"small"} className="custom-spin" />
                            </div>
                          ) : hasLeadingDecimalZeros(
                            String(feeAmount),
                            decimals || 4
                          ) ? (
                            <NotiWarningIcon
                              style={{ width: 16, height: 16 }}
                            />
                          ) : null}
                        </div>
                      </Tooltip>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Typography.Text
                          style={{
                            fontSize: isTablet ? 12 : 14,
                            fontWeight: 400,
                            color: "white",
                          }}
                        >
                          Fees:{" "}
                          {formatNumber(
                            Number(feeAmount),
                            decimals || selectedItem?.decimals || 4
                          )}{" "}
                          {symbolCurrency?.symbol || ""}
                        </Typography.Text>
                        {loadingFee ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Spin size={"small"} className="custom-spin" />
                          </div>
                        ) : null}
                      </div>
                    )}

                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleWithdrawalAll}
                    >
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 14 : 16,
                          fontWeight: 600,
                          color: "#1DABF1",
                        }}
                      >
                        All
                      </Typography.Text>
                    </div>
                  </div>
                </div>
                <div style={{ position: "absolute", top: -26, right: 0 }}>
                  <Typography.Text
                    style={{
                      fontSize: isTablet ? 12 : 14,
                      fontWeight: 400,
                      color: "white",
                    }}
                  >
                    Avl. balance:{" "}
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 400,
                        color: "white",
                      }}
                    >
                      {formatNumber(
                        Number(totalBalance || 0),
                        decimals || selectedItem?.decimals
                      )}{" "}
                      {symbolCurrency?.symbol || ""}
                    </Typography.Text>
                  </Typography.Text>
                </div>
              </Form.Item>
              {totalBalance === 0 || totalBalance < Number(minimumWithdrawal) ? (
                <Typography
                  className="text-withdraw-failed"
                  style={{
                    textAlign: "start",
                    marginTop: isTablet ? "-10px" : "-12px",
                  }}
                >
                  Your balance is below the minimum required to withdraw. Please
                  deposit more {symbolCurrency?.symbol} to continue.
                </Typography>
              ) : errorAmount ? (
                <Typography
                  className="text-withdraw-failed"
                  style={{
                    textAlign: "start",
                    marginTop: isTablet ? "-10px" : "-12px",
                  }}
                >
                  {errorAmount}
                </Typography>
              ) : (
                <Typography.Text
                  style={{
                    fontSize: isTablet ? 12 : 14,
                    fontWeight: 400,
                    color: "white",
                    marginTop: isTablet ? "-10px" : "-12px",
                  }}
                >
                  Beneficiary Will Receive:{" "}
                  {amountValue ? (
                    <Tooltip
                      placement="top"
                      title={
                        <span>
                          {beneficiaryGet} {symbolCurrency?.symbol || ""}
                        </span>
                      }
                    >
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 12 : 14,
                          color: "rgba(242, 138, 40, 1)",
                        }}
                      >
                        {beneficiaryGet.toFixed(
                          decimals || selectedItem?.decimals || 4
                        )}{" "}
                        {symbolCurrency?.symbol || ""}
                      </Typography.Text>
                    </Tooltip>
                  ) : (
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        color: "rgba(242, 138, 40, 1)",
                      }}
                    >
                      {beneficiaryGet.toFixed(
                        decimals || selectedItem?.decimals || 4
                      )}{" "}
                      {symbolCurrency?.symbol || ""}
                    </Typography.Text>
                  )}
                </Typography.Text>
              )}
              <Form.Item label="Note" name="note">
                <TextArea
                  className="withdraw-address"
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  placeholder="Enter a note (optional)"
                  onChange={(e) => {
                    form?.setFieldValue("note", e?.target.value);
                  }}
                />
              </Form.Item>
            </div>
            <div className="modal-withdraw-footer">
              {errorFee ? (
                <Typography
                  className="text-withdraw-failed"
                  style={{
                    textAlign: "center",
                    marginTop: isTablet ? "-10px" : "-12px",
                  }}
                >
                  {errorFee}
                </Typography>
              ) : null}
              <CustomButton
                type="submit"
                size={isTablet ? "medium" : "large"}
                text="Withdraw"
                width={"100%"}
                disabled={
                  !isAllFieldsFilled ||
                  loadingFee ||
                  loadingUserCode ||
                  form.getFieldError("smartUID")?.length > 0 ||
                  !!errorAmount ||
                  form.getFieldError("withdrawAddress")?.length > 0 ||
                  !!errorFee ||
                  loadingValidate
                }
              />
            </div>
          </Form>
        </Modal>
      ) : null}
      <ModalConfirm
        open={!!valueConfirm && !warningChange && !isLoading}
        handleCancelConfirm={handleCancelConfirm}
        onSubmitWithdraw={onSubmitWithdraw}
        amountValue={amountValue}
        currency={symbolCurrency?.symbol}
        metadataNetwork={metadataNetwork}
        selectedNetwork={selectedNetwork}
        transferMode={transferMode}
        addressValue={addressValue}
        userCode={userCode}
        isLoading={isLoading}
        failedMessage={failedMessage}
        submitFee={submitFee}
        warningChange={warningChange}
        decimals={decimals}
        infoByUserCode={infoByUserCode}
      />
      <LoadingOverlay loading={isLoading} />
      <ConfirmModal
        type="warning"
        title="Please Confirm Changes"
        description="Fee or receive amount has been updated. Take a moment to review before continuing."
        open={warningChange}
        onCancel={handleCancelConfirm}
        onSubmit={handleContinueReview}
        isLoading={false}
        textSubmit="Review"
      />
    </>
  );
};

export default ModalWithdraw;
